import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { Check, Dumbbell } from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '../ui/carousel'
import { DashboardSection } from './DashboardSection'
import type { DashboardWorkout } from '../../types/dashboard'

/** Centred so the workout it opens on sits mid-strip; dragFree drops the snap
 *  points, so the week scrolls without anchoring. */
const FREE_SCROLL = { align: 'center', dragFree: true, containScroll: 'trimSnaps' } as const

/** Day/month, the format the dashboard shows dates in. The endpoint only sends
 *  dated workouts, but the Workout type allows a null date. */
function dayMonth(iso: string | null): string {
  return iso ? format(parseISO(iso), 'dd/MM') : ''
}

interface WeekWorkoutsProps {
  workouts: DashboardWorkout[]
  /** Today, ISO — the day the "start training" button opens the diary on. */
  today: string
}

export function WeekWorkouts({ workouts, today }: WeekWorkoutsProps) {
  const { t } = useTranslation()
  const [api, setApi] = useState<CarouselApi>()

  // The next workout: the first one still to come. A week already behind us
  // opens on its last day rather than snapping back to Monday.
  const nextIndex = useMemo(() => {
    const index = workouts.findIndex((w) => w.scheduled_date != null && w.scheduled_date >= today)
    return index === -1 ? Math.max(0, workouts.length - 1) : index
  }, [workouts, today])

  useEffect(() => {
    if (!api) return
    api.scrollTo(nextIndex, true)
  }, [api, nextIndex])

  if (workouts.length === 0) {
    return (
      <DashboardSection title={t('dashboard.workouts.thisWeek')}>
        <Button asChild size="lg" className="h-16 w-full text-base">
          {/* Lands on the diary with the add-workout dialog already open on today. */}
          <Link to={`/training-diary?add_workout=${today}`}>
            <Dumbbell className="mr-2 size-5" />
            {t('dashboard.workouts.startTraining')}
          </Link>
        </Button>
      </DashboardSection>
    )
  }

  return (
    <DashboardSection title={t('dashboard.workouts.thisWeek')}>
      <Carousel opts={FREE_SCROLL} setApi={setApi} className="w-full">
        <CarouselContent className="-ml-2">
          {workouts.map((workout) => (
            <CarouselItem key={workout.id} className="basis-1/3 pl-2">
              <Link to={`/training-diary/workouts/${workout.id}`} className="block h-full">
                <Card className="relative h-full transition-colors hover:border-primary">
                  <CardContent className="flex h-full flex-col gap-2 p-2">
                    <span className="text-xs font-semibold text-primary">
                      {dayMonth(workout.scheduled_date)}
                    </span>
                    {workout.exercise.image_url ? (
                      <img
                        src={workout.exercise.image_url}
                        alt=""
                        className="aspect-square w-full rounded-md bg-muted object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex aspect-square w-full items-center justify-center rounded-md bg-muted">
                        <Dumbbell className="size-6 text-muted-foreground" />
                      </div>
                    )}
                    <span className="line-clamp-2 text-xs font-medium leading-snug">
                      {workout.exercise.title}
                    </span>
                  </CardContent>

                  {workout.completed && (
                    <span
                      className="absolute bottom-1 right-1 flex size-5 items-center justify-center rounded-full bg-green-600 text-white"
                      title={t('dashboard.workouts.completed')}
                    >
                      <Check className="size-3.5" strokeWidth={3} />
                      <span className="sr-only">{t('dashboard.workouts.completed')}</span>
                    </span>
                  )}
                </Card>
              </Link>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </DashboardSection>
  )
}
